import type { EventManager } from '@arkturian/audio-guide';
import type { RideMode, TransportMode } from '@arkturian/audio-guide';
import type { TrackingSnapshot } from '@arkturian/audio-guide';
import type { IView } from '../IView';

/**
 * ControlsView — binds all the action buttons + the transport and
 * theme selectors. Emits user-intent events on the bus; the consumer's
 * wireControls() bridges them to the imperative AudioGuide methods.
 */
export class ControlsView implements IView {
  private _startBtn: HTMLButtonElement | null = null;
  private _stopBtn: HTMLButtonElement | null = null;
  private _driftBtn: HTMLButtonElement | null = null;
  private _micBtn: HTMLButtonElement | null = null;
  private _moreBtn: HTMLButtonElement | null = null;
  private _pauseBtn: HTMLButtonElement | null = null;
  private _rebootBtn: HTMLButtonElement | null = null;
  private _osmToggle: HTMLButtonElement | null = null;
  private _osmRadiusSlider: HTMLInputElement | null = null;
  private _osmRadiusLabel: HTMLElement | null = null;
  private _osmEnabled = false;
  private _modeBtns: HTMLButtonElement[] = [];
  private _transportBtns: HTMLButtonElement[] = [];
  private _bus: EventManager | null = null;

  private _handlers: { el: HTMLElement; ev: string; fn: EventListener }[] = [];
  /** True if user clicked Pause (not Stop) — session is suspended but pool still acquired */
  private _isPaused = false;

  private _updateButtonState = (isRunning: boolean): void => {
    if (!this._startBtn || !this._stopBtn) return;
    // 3 states: IDLE (show Start) / RUNNING (show Pause + Stop) / PAUSED (show Resume + Stop)
    if (isRunning) {
      // Running — Pause + Stop visible, Start hidden
      this._startBtn.style.display = 'none';
      this._stopBtn.style.display = 'block';
      if (this._pauseBtn) {
        this._pauseBtn.style.display = 'block';
        this._pauseBtn.textContent = '⏸ Pause';
      }
      this._isPaused = false;
    } else if (this._isPaused) {
      // Paused — Resume + Stop visible, Start hidden
      this._startBtn.style.display = 'none';
      this._stopBtn.style.display = 'block';
      if (this._pauseBtn) {
        this._pauseBtn.style.display = 'block';
        this._pauseBtn.textContent = '▶ Weiter';
      }
    } else {
      // Idle — only Start visible
      this._startBtn.style.display = 'block';
      this._stopBtn.style.display = 'none';
      if (this._pauseBtn) this._pauseBtn.style.display = 'none';
    }
  };

  private _onTrackingChanged = (snap: TrackingSnapshot): void => {
    this._updateButtonState(snap.isRunning);
    // Theme button active state
    for (const btn of this._modeBtns) {
      const mode = btn.dataset.mode as RideMode | undefined;
      if (mode === snap.currentMode) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    }
  };

  private _onTransportChanged = (payload: { transport: TransportMode }): void => {
    for (const btn of this._transportBtns) {
      const t = btn.dataset.transport as TransportMode | undefined;
      if (t === payload.transport) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    }
  };

  mount(root: HTMLElement): void {
    this._startBtn = root.querySelector<HTMLButtonElement>('#startBtn');
    this._stopBtn = root.querySelector<HTMLButtonElement>('#stopBtn');
    this._driftBtn = root.querySelector<HTMLButtonElement>('#driftBtn');
    this._micBtn = root.querySelector<HTMLButtonElement>('#micBtn');
    this._moreBtn = root.querySelector<HTMLButtonElement>('#moreBtn');
    this._pauseBtn = root.querySelector<HTMLButtonElement>('#pauseBtn');
    this._rebootBtn = root.querySelector<HTMLButtonElement>('#rebootBtn');
    this._osmToggle = root.querySelector<HTMLButtonElement>('#osmToggle');
    this._osmRadiusSlider = root.querySelector<HTMLInputElement>('#osmRadiusSlider');
    this._osmRadiusLabel = root.querySelector<HTMLElement>('#osmRadiusLabel');
    this._modeBtns = Array.from(root.querySelectorAll<HTMLButtonElement>('.mode-btn'));
    this._transportBtns = Array.from(root.querySelectorAll<HTMLButtonElement>('.transport-btn'));
  }

  bindEvents(bus: EventManager): void {
    this._bus = bus;

    const bind = (el: HTMLElement | null, ev: string, fn: EventListener) => {
      if (!el) return;
      el.addEventListener(ev, fn);
      this._handlers.push({ el, ev, fn });
    };

    bind(this._startBtn, 'click', () => {
      // Start from idle — not paused anymore
      this._isPaused = false;
      bus.emit('user:start');
    });
    bind(this._stopBtn, 'click', () => {
      // Stop clears paused state
      this._isPaused = false;
      bus.emit('user:stop');
    });
    bind(this._pauseBtn, 'click', () => {
      // Toggle paused state LOCALLY before emitting — the _updateButtonState
      // called from tracking:changed will then show Resume instead of Pause
      this._isPaused = !this._isPaused;
      bus.emit('user:pause');
    });
    bind(this._driftBtn, 'click', () => bus.emit('user:drift-request'));
    bind(this._micBtn, 'click', () => bus.emit('user:mic-toggle'));
    bind(this._moreBtn, 'click', () => bus.emit('user:more-like-this'));
    bind(this._rebootBtn, 'click', () => bus.emit('user:reboot-guide'));

    // Theme buttons
    for (const btn of this._modeBtns) {
      const mode = btn.dataset.mode as RideMode | undefined;
      if (!mode) continue;
      bind(btn, 'click', () => bus.emit('user:mode-change', { mode }));
    }

    // Transport buttons
    for (const btn of this._transportBtns) {
      const transport = btn.dataset.transport as TransportMode | undefined;
      if (!transport) continue;
      bind(btn, 'click', () => bus.emit('user:transport-change', { transport }));
    }

    // OSM toggle + radius slider
    bind(this._osmToggle, 'click', () => {
      this._osmEnabled = !this._osmEnabled;
      bus.emit('user:osm-toggle', { enabled: this._osmEnabled });
      this._updateOsmUI();
    });
    bind(this._osmRadiusSlider, 'input', () => {
      const val = parseInt(this._osmRadiusSlider!.value, 10);
      if (this._osmRadiusLabel) this._osmRadiusLabel.textContent = `${val}m`;
      bus.emit('user:osm-radius', { radius: val });
    });

    // Places context ON by default (disable via ?osm=0)
    const params = new URLSearchParams(window.location.search);
    if (params.get('osm') !== '0') {
      this._osmEnabled = true;
      this._updateOsmUI();
      bus.emit('user:osm-toggle', { enabled: true });
    }

    bus.on('tracking:changed', this._onTrackingChanged);
    bus.on('audio-guide:transport-changed', this._onTransportChanged);
  }

  private _updateOsmUI(): void {
    if (this._osmToggle) {
      this._osmToggle.textContent = this._osmEnabled ? '📍 Places: On' : '📍 Places: Off';
      this._osmToggle.style.background = this._osmEnabled ? '#1a3a1a' : '#1a1b26';
      this._osmToggle.style.color = this._osmEnabled ? '#9ece6a' : '#565f89';
      this._osmToggle.style.borderColor = this._osmEnabled ? '#9ece6a' : '#414868';
    }
    if (this._osmRadiusSlider) {
      this._osmRadiusSlider.style.display = this._osmEnabled ? 'block' : 'none';
      this._osmRadiusSlider.disabled = !this._osmEnabled;
    }
    if (this._osmRadiusLabel) {
      this._osmRadiusLabel.style.display = this._osmEnabled ? 'block' : 'none';
    }
  }

  dispose(): void {
    for (const h of this._handlers) {
      h.el.removeEventListener(h.ev, h.fn);
    }
    this._handlers = [];
    if (this._bus) {
      this._bus.off('tracking:changed', this._onTrackingChanged);
      this._bus.off('audio-guide:transport-changed', this._onTransportChanged);
      this._bus = null;
    }
  }
}
